import { IWidget, IWidgetFactory } from '../../widgets';
import { OptionalColumns } from 'components';
import { IWidgetWithTemplate, ISeriesWidget } from '.';
import { IWidgetComponentConfiguration } from './widgetComponentConfiguration';

export interface IWidgetConfigurationService {
    getWidgetConfiguration(item: IWidget): IWidgetComponentConfiguration;
}

export class WidgetConfigurationService implements IWidgetConfigurationService {
    static serviceId = 'widgetConfigurationService';

    sizes = {
        trend: {
            x: 6,
            y: 8,
            col: 6,
            row: 0
        },
        select: {
            x: 6,
            y: 8,
            col: 0,
            row: 0
        },
        series: {
            x: 12,
            y: 4,
            col: 0,
            row: 9
        }
    };

    static $inject = [
        '$window',
        'widgetFactory'
    ];

    constructor(
        private _$window: angular.IWindowService,
        private widgetFactory: IWidgetFactory
    ) {
    }

    getWidgetConfiguration(widget: IWidget): IWidgetComponentConfiguration {
        let result: IWidgetComponentConfiguration = null;
        switch (widget.type) {
            case 'businessKpi':
                result = this.createBusinessKpiView(widget);
                break;
            case 'tagsTrend':
                result = this.createTagsTrendView(widget);
                break;
            case 'scatter':
                result = this.createScatterPlotView(widget);
                break;
            case 'text':
                result = this.createTextView(widget);
                break;
            case 'table':
                result = this.createTableView(widget);
                break;
            case 'chartByIdentifier':
                result = this.createChartByIdentifier(widget);
                break;
            case 'DoughnutChart':
                result = this.createDoughnutChart(widget);
                break;
            case 'GroupStates':
                result = this.createGroupStatesConfiguration(widget);
                break;
            case 'TableByGroup':
                result = this.createTableByGroupConfiguration(widget);
                break;
            case 'FlowChart':
                result = this.createFlowChartConfiguration(widget);
                break;
            case 'ReconciliationMeasure':
                result = {
                    widgets: {
                        right: this.createPanelWidget('ReconciliationMeasure',
                            '<wt-widget-panel item="item" edited-item="editedItem"><wt-reconciliation-measure item="item" class="flex-directive"></wt-reconciliation-measure></wt-widget-panel>')
                    },
                    contextual: {
                        template:
`<wt-modern-reconciliation-measure-contextual cancel="cancel()"
                                              configuration="item.configuration" 
                                              form-controller="formController"
                                              groups="groups"
                                              save="save()">
</wt-modern-reconciliation-measure-contextual>`,
                        migrated: true
                    }
                };
                break;
            case 'LogbookTable':
                const widgetWithTemplate = this.createPanelWidget('LogbookTable',
                    '<wt-widget-panel item="item"><wt-logbook-table item="editedItem" class="flex-directive" bound-series="editedItem.series" bound-data="editedItem.data" dashboards="editedItem.dashboards" string-values="stringValues"></wt-logbook-table></wt-widget-panel>');
                result = {
                    widgets: {
                        left: this.getSeriesSelectionPanel(),
                        right: widgetWithTemplate,
                        bottom: this.createSerieWidget(
                            `<wt-widget-panel item="item" edited-item="editedItem" class="flex-single">
                                <wt-logbook-table-series class="flex-directive" form-controller="formController" is-bindable="isBindable" series="editedItem.configuration.series">
                                </wt-logbook-table-series>
                            </wt-widget-panel>`
                        )
                    },
                    contextual: {
                        template: '<wt-logbook-table-contextual configuration="item.configuration" form-controller="formController" dashboards="dashboards"></wt-logbook-table-contextual>'
                    }
                };
                break;
            case 'AlarmsHeatMap':
                result = {
                    widgets: {
                        right: this.createPanelWidget('AlarmsHeatMap', '<wt-widget-panel item="item"><wt-alarms-heat-map item="editedItem" class="flex-directive" bound-data="editedItem.data"></wt-alarms-heat-map></wt-widget-panel>')
                    },
                    contextual: {
                        template: '<wt-alarms-heat-map-contextual is-bindable="isBindable" configuration="item.configuration" form-controller="formController" dashboards="dashboards"></wt-alarms-heat-map-contextual>',
                        hideError: true
                    }
                };
                break;
            case 'AlarmsTable':
                result = {
                    widgets: { right: this.createPanelWidget('AlarmsTable', '<wt-widget-panel item="item"><wt-alarms-table item="editedItem" class="flex-directive"></wt-alarms-table></wt-widget-panel>') },
                    contextual: {
                        template: '<wt-alarms-table-contextual configuration="item.configuration" form-controller="formController"></wt-alarms-table-contextual>'
                    }
                };
                break;
            case 'Tile':
                result = {
                    widgets: {
                        left: this.getSeriesSelectionPanel(),
                        right: this.createPanelWidget('Tile',
                            `<wt-widget-panel item="item" style="height: ${this.getApproximateSameHeight(widget)}px"><wt-tile-widget item="editedItem" class="flex-directive" is-bindable="isBindable" series="editedItem.configuration.series" boundData="data" view-models="viewModels" digital-sets="digitalSetDictionary" string-values="stringValues"></wt-widget-panel>`
                        ),
                        bottom: this.createSerieWidget(
                            '<wt-widget-panel item="item" edited-item="editedItem" class="flex-single"><wt-tile-series is-bindable="isBindable" class="flex-directive" series="editedItem.configuration.series" view-models="viewModels"></wt-tile-series></wt-widget-panel>'
                        )
                    },
                    contextual: {
                        template: '<wt-tile-contextual configuration="item.configuration" form-controller="formController"></wt-tile-contextual>'
                    }
                };
                break;
            case 'approvalTable':
                result = {
                    widgets: {
                        right: this.createPanelWidget('approvalTable',
                            '<wt-widget-panel item="item"><wt-approval-table item="editedItem" class="flex-directive" bound-data="editedItem.data"></wt-approval-table></wt-widget-panel>')
                    },
                    contextual: {
                        template: '<wt-approval-table-contextual configuration="item.configuration" form-controller="formController" groups="groups" is-bindable="isBindable"></wt-approval-table-contextual>'
                    }
                };
                result.widgets.right.x = 0;
                result.widgets.right.y = 0;
                result.widgets.right.col = 12;
                result.widgets.right.row = 12;
                break;
            case 'gaugeChart':
                result = this.createGaugeChart(widget);
                break;
            case 'qualityBadActors':
                result = {
                    widgets: {
                        right:
                            this.createPanelWidget(widget.type,
                                `
<div class="panel panel-hide-focus widget-panel">
    <div class="panel-heading">
        <span>
            {{editedItem.name}}
        </span>
    </div>
    <div class="panel-body">
        <wt-quality-bad-actors class="flex-directive"
                               item="editedItem"
                               bound-data="editedItem.data"
                               tags-dictionary="tagsDictionary">
        </wt-quality-bad-actors>
    </div>
</div>
`)
                    },
                    contextual: {
                        template: `
<wt-quality-bad-actors-contextual configuration="item.configuration" 
                                  form-controller="formController"
                                  save="save()" 
                                  cancel="cancel()">
</wt-quality-bad-actors-contextual>`,
                        migrated: true
                    }
                };
                break;
            case 'button':
                result = {
                    widgets: {
                        right:
                            this.createPanelWidget(widget.type,
                                `
    <div class="panel panel-hide-focus widget-panel">
        <div class="panel-heading">
            <span>
                {{editedItem.name}}
            </span>
        </div>
        <div class="panel-body">
            <wt-button  class="flex-directive"
                        item="editedItem">
            </wt-button>
        </div>
    </div>
    `)
                    },
                    contextual: {
                        template: `
    <wt-button-contextual configuration="item.configuration" 
                          form-controller="formController"
                          save="save()" 
                          cancel="cancel()">
    </wt-button-contextual>`,
                        migrated: true
                    }
                };
                break;
            case 'pivotTable':
                result = this.createPivotTable(widget);
                break;
            case 'spotValuesWidget':
                result = this.createSpotValuesWidget(widget);
                break;
            default:
                break;
        }
        return result;
    }

    private createSpotValuesWidget(item: IWidget): IWidgetComponentConfiguration {
        return {
            widgets: {
                right: this.createPanelWidget(
                    item.type,
                    `<wt-widget-panel item="item" edited-item="editedItem">
                        <wt-spot-values-widget class="flex-directive"
                                               bound-data="editedItem.data.values"
                                               current-group-guid="referenceGroupGuid"
                                               digital-sets="digitalSetDictionary"
                                               groups="groups"
                                               item="editedItem"
                                               string-values="stringValues"
                                               view-models="viewModels">
                        </wt-spot-values-widget>
                     </wt-widget-panel>`
                )
            },
            contextual: {
                template: `<wt-spot-values-widget-contextual configuration="item.configuration" 
                                                             form-controller="formController"
                                                             cancel="cancel()"
                                                             groups="groups"
                                                             save="save()" 
                                                             view-models="viewModels">
                           </wt-spot-values-widget-contextual>`,
                migrated: true
            }
        };
    }

    private createPivotTable(item: IWidget): IWidgetComponentConfiguration {
        const result = {
            widgets: {
                right:
                    this.createPanelWidget(item.type,
                        `<wt-widget-panel item="item" edited-item="editedItem">
                            <wt-pivot-table class="flex-directive"
                                            bound-data="editedItem.data"
                                            digital-set-dictionary="digitalSetDictionary"
                                            item="editedItem"
                                            string-values="stringValues"
                                            view-models="viewModels">
                            </wt-pivot-table>
                        </wt-widget-panel>
                        `),
                left: this.getSeriesSelectionPanel(null, false, true),
                bottom: this.createSerieWidget(
                    `<wt-widget-panel item="item" 
          edited-item="editedItem" 
          class="flex-single">
    <wt-pivot-table-series-definition class="flex-directive"
                                is-bindable="isBindable"
                                series="editedItem.configuration.series"
                                view-models="viewModels">
    </wt-pivot-table-series-definition>
</wt-widget-panel>`
                ),
                bottomLeft: {
                    name: 'Row series',
                    template:
                        `<wt-widget-panel item="item" 
          edited-item="editedItem" 
          class="flex-single">
    <wt-pivot-table-rows-definition class="flex-directive"
                                bound-data="editedItem.data"
                                is-bindable="isBindable"
                                row-series="editedItem.configuration.rowSeries"
                                view-models="viewModels">
    </wt-pivot-table-rows-definition>
</wt-widget-panel>`
                }
            },
            contextual: {
                template: `
<wt-pivot-table-contextual configuration="item.configuration" 
                              form-controller="formController"
                              save="save()" 
                              cancel="cancel()"
                              view-models="viewModels">
</wt-pivot-table-contextual>`,
                migrated: true
            }
        };
        return result;
    }

    private createGaugeChart(item: IWidget): IWidgetComponentConfiguration {
        const optionalColumns: OptionalColumns = {
            minMax: true,
            stepped: false
        };
        // same as chart by identifier since configuration should be interchangeable
        const serieWidget = this.createChartByIdentifierSeriesPanel(optionalColumns);
        const preview = this.createPanelWidget(
            'gaugeChart',
            '<wt-widget-panel item="item"><wt-gauge-chart item="editedItem" bound-data="editedItem.data" bound-series="editedItem.series" class="flex-directive"></wt-gauge-chart></wt-widget-panel>'
        );

        const result: IWidgetComponentConfiguration = {
            widgets: {
                left: this.getSeriesSelectionPanel(),
                right: preview,
                bottom: serieWidget
            },
            contextual: {
                template: '<wt-gauge-chart-contextual configuration="item.configuration" form-controller="formController" is-bindable="isBindable" save="save()" cancel="cancel()"></wt-gauge-chart-contextual>',
                migrated: true
            }
        };
        result.widgets.right.sizeX = 0;
        result.widgets.right.sizeY = 0;
        result.widgets.right.col = 12;
        result.widgets.right.row = 12;
        return result;
    }

    private createChartByIdentifierSeriesPanel(optionalColumns: OptionalColumns) {
        const serieWidget = this.createSerieWidget(
            `<wt-widget-panel item="item" edited-item="editedItem">
                <wt-series-chart-by-identifier item="editedItem" 
                                               series="editedItem.configuration.series" 
                                               is-bindable="isBindable" 
                                               optional-columns="${JSON.stringify(optionalColumns).replace(/\"/g, '\'')}">
                </wt-series-chart-by-identifier>
            </wt-widget-panel>`
        );
        return serieWidget;
    }

    private createBusinessKpiView(item: IWidget): IWidgetComponentConfiguration {
        const w = this.createPanelWidget('businessKpi',
            '<wt-widget-panel item="item"><wt-business-kpi item="editedItem" class="flex-directive"></wt-business-kpi></wt-widget-panel>');
        w.configuration = item.configuration;

        const seriesSelection = this.getSeriesSelectionPanel();
        const serieWidget = this.createSerieWidget('<wt-widget-panel item="item" edited-item="editedItem" class="flex-single"><wt-business-kpi-series item="editedItem" is-bindable="isBindable" class="flex-directive" groups="groups" view-models="viewModels"></wt-business-kpi-series></wt-widget-panel>');
        const result = {
            widgets: {
                left: seriesSelection,
                right: w,
                bottom: serieWidget
            },
            contextual: {
                template: '<wt-business-kpi-contextual configuration="item.configuration" form-controller="formController" save="save()" cancel="cancel()"></wt-business-kpi-contextual>',
                migrated: true,
            }
        };
        return result;
    }

    private createTagsTrendView(item: IWidget): IWidgetComponentConfiguration {
        const preview = this.createPanelWidget(
            'tagsTrend',
            '<wt-widget-panel item="item"><wt-bound-tags-trend item="editedItem" class="flex-directive" series="editedItem.series" bound-data="editedItem.data"></wt-bound-tags-trend></wt-widget-panel>'
        );
        const serieWidget = this.createSerieWidget(
            '<wt-widget-panel item="item" edited-item="editedItem"><wt-series-table type="trend" series="editedItem.configuration.series" is-bindable="isBindable" form-controller="formController" has-offset="true"></wt-series-table></wt-widget-panel>'
        );

        const seriesSelection = this.getSeriesSelectionPanel();
        seriesSelection.sizeX = this.sizes.select.x;
        seriesSelection.sizeY = this.sizes.select.y;

        const result = {
            widgets: {
                left: seriesSelection,
                right: preview,
                bottom: serieWidget
            },
            contextual: {
                template: '<wt-tags-trend-contextual form-controller="formController" configuration="item.configuration" save="save()" cancel="cancel()"></wt-tags-trend-contextual>',
                migrated: true
            }
        };
        return result;
    }

    private createChartByIdentifier(item: IWidget): IWidgetComponentConfiguration {
        const preview = this.createPanelWidget('chartByIdentifier',
            '<wt-widget-panel item="item"><wt-chart-by-identifier item="editedItem" class="flex-directive" is-bound="true" bound-data="editedItem.data" bound-series="editedItem.series"></wt-chart-by-identifier></wt-widget-panel>');
        preview.configuration = item.configuration;

        const seriesSelection = this.getSeriesSelectionPanel();
        const optionalColumns: OptionalColumns = {
            stepped: true,
            minMax: true
        };
        const serieWidget = this.createChartByIdentifierSeriesPanel(optionalColumns);
        const result = {
            widgets: {
                left: seriesSelection,
                right: preview,
                bottom: serieWidget
            },
            contextual: {
                template: '<wt-chart-by-identifier-contextual configuration="item.configuration" form-controller="formController" save="save()" cancel="cancel()"></wt-chart-by-identifier-contextual>',
                migrated: true
            }
        };
        return result;
    }

    private createDoughnutChart(item: IWidget): IWidgetComponentConfiguration {
        const preview = this.createPanelWidget('DoughnutChart',
            '<wt-widget-panel item="item"><wt-doughnut-chart-widget item="editedItem" class="flex-directive" bound-series="editedItem.series" bound-data="editedItem.data"></wt-doughnut-chart-widget></wt-widget-panel>');
        preview.configuration = item.configuration;

        const seriesSelection = this.getSeriesSelectionPanel();
        const optionalColumns: OptionalColumns = {
            minMax: false,
            stepped: false
        };
        const serieWidget = this.createChartByIdentifierSeriesPanel(optionalColumns);
        const result = {
            widgets: {
                left: seriesSelection,
                right: preview,
                bottom: serieWidget
            },
            contextual: {
                template: '<wt-doughnut-chart-widget-contextual configuration="item.configuration" form-controller="formController" save="save()" cancel="cancel()"></wt-doughnut-chart-widget-contextual>',
                migrated: true
            }
        };
        return result;
    }

    private createScatterPlotView(item: IWidget): IWidgetComponentConfiguration {
        const preview = this.createPanelWidget(
            'scatter',
            '<wt-widget-panel item="item"><wt-scatter item="editedItem" disable-crosshair="true" view-models="viewModels"></wt-scatter></wt-widget-panel>'
        );
        const serieWidget = this.createSerieWidget(
            '<wt-widget-panel item="item" edited-item="editedItem"><wt-series-table type="scatter" series="editedItem.configuration.series" is-bindable="isBindable"></wt-series-table></wt-widget-panel>'
        );
        serieWidget.hasShapes = true;
        const seriesSelection = this.getSeriesSelectionPanel();

        const result = {
            widgets: {
                left: seriesSelection,
                right: preview,
                bottom: serieWidget
            },
            contextual: {
                template: '<wt-scatter-contextual form-controller="formController" configuration="item.configuration" save="save()" cancel="cancel()"></wt-scatter-contextual>',
                migrated: true
            }
        };
        return result;
    }

    private getSeriesSelectionPanel(excludeTags?: boolean, includeQueries?: boolean, disablePlus?: boolean): IWidgetWithTemplate {
        const seriesSelection = this.widgetFactory.createWidget('seriesSelection') as IWidgetWithTemplate;
        seriesSelection.template =
            `<wt-series-selection-panel item="item" 
                            is-bindable="isBindable" 
                            exclude-tags="${!!excludeTags}" 
                            include-queries="${!!includeQueries}" 
                            class="flex-directive"
                            disable-plus=${!!disablePlus}>
</wt-series-selection-panel>`;
        seriesSelection.col = this.sizes.select.col;
        seriesSelection.row = this.sizes.select.row;
        seriesSelection.sizeX = this.sizes.select.x;
        seriesSelection.sizeY = this.sizes.select.y;
        return seriesSelection;
    }

    private createTextView(item: IWidget): IWidgetComponentConfiguration {
        const widgetList = [];
        const w: IWidgetWithTemplate = this.widgetFactory.createWidget('text') as IWidgetWithTemplate;
        w.template =
            `<div class="flex-row-xs-12">
   <div class="flex-col-xs-6">
        <wt-widget-panel item="{name: \'Editor\'}" 
                         edited-item="editedItem">
            <wt-modern-markdown-editor class="flex-directive" 
                                       model="editedItem.configuration.text" 
                                       theme="editedItem.configuration.theme"
                                       application-guid="applicationGuid">
            </wt-modern-markdown-editor>
        </wt-widget-panel>
    </div>
    <div class="flex-col-xs-6">
        <wt-widget-panel item="{name:\'Preview\'}">
            <wt-text item="editedItem" class="flex-directive">
            </wt-text>
        </wt-widget-panel>
    </div>
</div>`            ;
        widgetList.push(w);

        const result = {
            class: 'text-view-widget',
            widgets: {
                left: undefined,
                right: w
            },
            contextual: {
                template: '<wt-text-contextual form-controller="formController" configuration="item.configuration" save="save()" cancel="cancel()"></wt-text-contextual>',
                migrated: true
            }
        };
        return result;
    }

    private createTableView(item: IWidget): IWidgetComponentConfiguration {
        const preview = this.createPanelWidget(
            'table',
            '<wt-widget-panel item="item" class="flex-transclude"><wt-table item="editedItem" class="flex-directive" bound-data="editedItem.data" is-bound="true" statuses="editedItem.statuses" row-view-models="editedItem.series" string-values="stringValues"></wt-table></wt-widget-panel>'
        );
        const serieWidget =
            this.createSerieWidget(
                '<wt-widget-panel item="item" edited-item="editedItem"><wt-table-rows rows="editedItem.configuration.rows" is-bindable="isBindable" class="full-height" form-controller="formController"></wt-table-rows></wt-widget-panel>');
        const seriesSelection = this.getSeriesSelectionPanel(null, true);

        const result = {
            widgets: {
                left: seriesSelection,
                right: preview,
                bottom: serieWidget
            },
            contextual: {
                template: '<wt-table-contextual form-controller="formController" configuration="item.configuration" save="save()" cancel="cancel()"></wt-table-contextual>',
                migrated: true
            }
        };
        return result;
    }

    private assignTrendSizes(w: IWidgetWithTemplate) {
        w.col = this.sizes.trend.col;
        w.row = this.sizes.trend.row;
        w.sizeX = this.sizes.trend.x;
        w.sizeY = this.sizes.trend.y;
    }

    private createPanelWidget(type: string, template: string) {
        const w = this.widgetFactory.createWidget(type) as IWidgetWithTemplate;
        w.template = template;
        this.assignTrendSizes(w);
        return w;
    }

    private createSerieWidget(template: string): ISeriesWidget {
        const series = {
            sizeX: this.sizes.series.x,
            sizeY: this.sizes.series.y,
            col: this.sizes.series.col,
            row: this.sizes.series.row,
            'type': '',
            name: 'Series',
            template: template
        };

        return series;
    }

    private createGroupStatesConfiguration(item: IWidget) {
        const preview = this.createPanelWidget(
            'GroupStates',
            '<wt-widget-panel item="item"><wt-group-states item="editedItem" groups="groups"></wt-group-states></wt-widget-panel>'
        );
        const serieWidget = this.createSerieWidget('<wt-widget-panel item="item" edited-item="editedItem"><wt-group-states-series is-bindable="isBindable" series="editedItem.configuration.series" groups="groups"></wt-group-states-series></wt-widget-panel>');
        const seriesSelection: IWidgetWithTemplate = {
            sizeX: this.sizes.select.x,
            sizeY: this.sizes.select.y,
            row: this.sizes.select.row,
            col: this.sizes.select.col,
            template: '<wt-widget-panel item="item" is-bindable="isBindable"><wt-group-selection></wt-group-selection> </wt-widget-panel>',
            name: 'Group selection',
            type: 'GroupSelection'
        };
        const result = {
            widgets: {
                left: seriesSelection,
                right: preview,
                bottom: serieWidget
            },
            contextual: {
                template: '<wt-group-states-contextual form-controller="formController" configuration="item.configuration" save="save()" cancel="cancel()"></wt-group-states-contextual>',
                migrated: true
            }
        };
        return result;
    }

    private createTableByGroupConfiguration(item: IWidget) {
        const preview = this.createPanelWidget(
            'TableByGroup',
            `<wt-widget-panel item="item">
                <wt-table-by-group class="flex-directive"
                                   bound-data="editedItem.data"
                                   digital-sets="digitalSetDictionary"
                                   groups="groups"
                                   item="editedItem"
                                   reference-group-guid="referenceGroupGuid"
                                   string-values="stringValues"
                                   view-models="viewModels">
                </wt-table-by-group>
             </wt-widget-panel>`
        );
        const serieWidget = this.createSerieWidget(
            `<wt-widget-panel item="item" edited-item="editedItem">
                <wt-table-by-group-series class="flex-directive"
                                          groups="groups"
                                          has-legend="editedItem.configuration.hasLegend"
                                          is-bindable="isBindable"
                                          series="editedItem.configuration.series"
                                          string-values="stringValues"
                                          view-models="viewModels">
                </wt-table-by-group-series>
            </wt-widget-panel>`
            );
        const seriesSelection = this.getSeriesSelectionPanel(true);
        const result = {
            widgets: {
                left: seriesSelection,
                right: preview,
                bottom: serieWidget
            },
            contextual: {
                migrated: true,
                template: `<wt-table-by-group-contextual configuration="item.configuration"
                                                         form-controller="formController"
                                                         is-bindable="isBindable"
                                                         form-controller="formController"
                                                         save="save()"
                                                         cancel="cancel()"
                                                         string-values="stringValues"
                                                         view-models="viewModels">
                           </wt-table-by-group-contextual>`
            }
        };
        return result;
    }

    private createFlowChartConfiguration(item: IWidget) {
        const preview = this.createPanelWidget(
            'FlowChart',
            `<wt-widget-panel item="item">
                <wt-flow-chart dashboards="dashboards"
                               digital-sets="digitalSetDictionary"
                               item="editedItem"
                               is-edit-mode="true"
                               string-values="stringValues"
                               view-models="viewModels">
                </wt-flow-chart>
             </wt-widget-panel>`
        );

        const serieWidget = this.createSerieWidget(
            '<wt-widget-panel item="item" edited-item="editedItem">' +
            '<wt-flow-chart-series series="editedItem.configuration.series" selected-serie="editedItem.configuration.selectedSerie" is-bindable="isBindable" class="full-height" view-models="viewModels"></wt-flow-chart-series>' +
            '</wt-widget-panel>');

        const seriesSelection = this.getSeriesSelectionPanel();

        const result = {
            widgets: {
                left: seriesSelection,
                right: preview,
                bottom: serieWidget
            },
            contextual: {
                template: '<wt-flow-chart-contextual form-controller="formController" configuration="item.configuration" save="save()" cancel="cancel()"></wt-flow-chart-contexual>',
                migrated: true
            }
        };
        return result;
    }

    private getApproximateSameHeight(item: IWidget): number {
        const result = this._$window.innerHeight * (item.sizeY / 36);
        return Math.max(result, 50);
    }
}